// User preferences stored in localStorage, keyed per operator alias.
//
// Why localStorage (not the backend): at POC scope we don't want a prefs
// table. If the operator clears browser storage, prefs reset to defaults —
// none of these values represent authoritative state.
//
// Keyed by alias so multiple operators using the same browser (or role
// switching that changes the alias focus) don't cross-pollute preferences.
// The key shape is `reb.ui.prefs.{alias}`.

export interface UserPrefs {
  advanced_mode_enabled: boolean;
}

export const DEFAULT_PREFS: UserPrefs = {
  advanced_mode_enabled: false,
};

export function prefsKey(alias: string): string {
  return `reb.ui.prefs.${alias}`;
}

/**
 * Narrow an unknown JSON blob into a `UserPrefs`. Each field falls back to
 * its default, which keeps parsing robust when the stored shape predates
 * the latest version.
 */
function coercePrefs(raw: unknown): UserPrefs {
  if (typeof raw !== "object" || raw === null) return { ...DEFAULT_PREFS };
  const obj = raw as Record<string, unknown>;
  return {
    advanced_mode_enabled:
      typeof obj.advanced_mode_enabled === "boolean"
        ? obj.advanced_mode_enabled
        : DEFAULT_PREFS.advanced_mode_enabled,
  };
}

/**
 * Load preferences for the given alias. SSR/jsdom safe: returns defaults
 * when `window` is unavailable or localStorage throws (private mode, quota).
 */
export function loadPrefs(alias: string): UserPrefs {
  if (typeof window === "undefined" || !window.localStorage) {
    return { ...DEFAULT_PREFS };
  }
  try {
    const raw = window.localStorage.getItem(prefsKey(alias));
    if (raw === null) return { ...DEFAULT_PREFS };
    return coercePrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

/**
 * Merge and persist a partial preferences update for the given alias.
 * Errors (quota exceeded, disabled storage) are swallowed — prefs are
 * ephemeral and a failed write must never break a mutation path.
 */
export function savePrefs(alias: string, partial: Partial<UserPrefs>): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const current = loadPrefs(alias);
    const next: UserPrefs = { ...current, ...partial };
    window.localStorage.setItem(prefsKey(alias), JSON.stringify(next));
  } catch {
    // Intentional — see jsdoc above.
  }
}
